import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Send, Loader2, Check, X as XIcon, Trash2, User as UserIcon, Bot,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ebayAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * EbayChatPanel — chat-based listing editor (founder only).
 *
 * User types plain-English changes ("set color grade to F", "shorten the title").
 * Claude proposes a typed action. User clicks Apply → it pushes to eBay.
 */
export default function EbayChatPanel({ product, onUpdate }) {
  const { isEbayUser } = useAuth();
  const founderView = isEbayUser?.();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!founderView) return;
    try {
      const res = await ebayAPI.getChat(product.id);
      setMessages(res.data.messages || []);
    } catch (err) {
      // silent fail — chat history is non-critical
    }
  }, [founderView, product.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput('');
    setSending(true);

    // Optimistic user message
    const tempUserId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempUserId, role: 'user', content: msg, created_at: new Date().toISOString() },
    ]);

    try {
      const res = await ebayAPI.sendChat(product.id, msg);
      // Reload from server to get authoritative IDs + assistant turn
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempUserId),
        res.data.user_turn,
        res.data.assistant_turn,
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chat failed');
      setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
    } finally {
      setSending(false);
    }
  };

  const handleApply = async (msg) => {
    if (!msg.proposed_change) return;
    setApplyingId(msg.id);
    try {
      const res = await ebayAPI.applyChange(product.id, msg.id, msg.proposed_change);
      const updates = res.data.updates || {};
      // Merge updates back into the product so the eBay panel + description editor re-render
      onUpdate({ ...product, ...updates });
      if (res.data.push_result?.updated) {
        toast.success(`Applied — ${res.data.push_result.variations_updated} variations updated on eBay`);
      } else {
        toast.success('Saved on product (no eBay listing yet)');
      }
      // Reload chat to pick up the synthetic confirmation message
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplyingId(null);
    }
  };

  const handleDismiss = (id) => {
    // Local-only — mark as applied/dismissed so the Apply button hides
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, applied: true } : m)));
  };

  const handleClearChat = async () => {
    if (!window.confirm('Clear the full chat history for this product?')) return;
    try {
      await ebayAPI.clearChat(product.id);
      setMessages([]);
      toast.success('Chat cleared');
    } catch (err) {
      toast.error('Clear failed');
    }
  };

  if (!founderView) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col" data-testid="ebay-chat-panel">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3 border-b border-zinc-100">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
            <Sparkles size={14} className="text-blue-600" /> Edit with AI
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Tell me what to change — title, description, or aspects (color, clarity, cut, etc.). I'll propose the edit, you approve, it goes live on eBay.
          </p>
        </div>
        {messages.length > 0 && (
          <Button
            variant="ghost" size="sm"
            onClick={handleClearChat}
            className="text-zinc-400 hover:text-red-600"
            data-testid="ebay-chat-clear-btn"
            title="Clear chat history"
          >
            <Trash2 size={13} />
          </Button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-3 max-h-[500px] min-h-[200px] bg-zinc-50/30"
        data-testid="ebay-chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-center py-12 space-y-2">
            <Sparkles size={20} className="text-blue-400 mx-auto" />
            <div className="text-xs text-zinc-500 max-w-sm mx-auto leading-relaxed">
              Try: <span className="text-zinc-700 italic">"Change the diamond color to F"</span> ·{' '}
              <span className="text-zinc-700 italic">"Shorten the title"</span> ·{' '}
              <span className="text-zinc-700 italic">"Add 'Vintage Inspired' to the title"</span> ·{' '}
              <span className="text-zinc-700 italic">"Set certification to GIA"</span>
            </div>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            applying={applyingId === m.id}
            onApply={() => handleApply(m)}
            onDismiss={() => handleDismiss(m.id)}
          />
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-zinc-400 text-xs px-2">
            <Loader2 size={12} className="animate-spin" /> Claude is thinking…
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-zinc-100 p-3 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Tell me what to change on this listing..."
            rows={2}
            className="flex-1 resize-none text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 placeholder:text-zinc-400"
            disabled={sending}
            data-testid="ebay-chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="ebay-chat-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>
        <div className="text-[10px] text-zinc-400 mt-1.5 px-1">
          Enter to send · Shift+Enter for newline
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message, applying, onApply, onDismiss }) {
  const isUser = message.role === 'user';
  const pc = message.proposed_change;
  const hasActionable = !isUser && pc && (pc.type === 'update_text' || pc.type === 'update_aspects') && !message.applied;
  const isConfirmation = message.is_system_confirmation;

  return (
    <div className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isUser ? 'bg-zinc-900 text-white' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
        {isUser ? <UserIcon size={12} /> : <Bot size={13} />}
      </div>
      <div className={`max-w-[78%] space-y-1 ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`px-3 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-zinc-900 text-white rounded-tr-sm'
            : isConfirmation
              ? 'bg-emerald-50 text-emerald-900 border border-emerald-100 rounded-tl-sm'
              : 'bg-white text-zinc-900 border border-zinc-200 rounded-tl-sm'
        }`}>
          {message.content}
        </div>
        {hasActionable && (
          <ProposedChangeCard
            change={pc}
            applying={applying}
            onApply={onApply}
            onDismiss={onDismiss}
            messageId={message.id}
          />
        )}
      </div>
    </div>
  );
}

function ProposedChangeCard({ change, applying, onApply, onDismiss, messageId }) {
  const isText = change.type === 'update_text';
  const isAspects = change.type === 'update_aspects';
  return (
    <div className="bg-blue-50/40 border border-blue-100 rounded-xl p-3 space-y-2 max-w-md" data-testid={`ebay-chat-proposal-${messageId}`}>
      <div className="text-[11px] uppercase tracking-wider text-blue-600 font-semibold">
        {isText ? 'Proposed text change' : isAspects ? 'Proposed aspect change' : 'Proposed change'}
      </div>
      {isText && (
        <div className="space-y-1.5">
          {change.title && (
            <div>
              <div className="text-[10px] text-zinc-400 uppercase">New title ({change.title.length}/80)</div>
              <div className="text-[12px] text-zinc-900 leading-snug break-words">{change.title}</div>
            </div>
          )}
          {change.description && (
            <div>
              <div className="text-[10px] text-zinc-400 uppercase">New description</div>
              <div className="text-[12px] text-zinc-700 leading-snug whitespace-pre-wrap line-clamp-6">
                {change.description}
              </div>
            </div>
          )}
        </div>
      )}
      {isAspects && (
        <div className="space-y-1.5">
          {change.aspects_set && Object.keys(change.aspects_set).length > 0 && (
            <div>
              <div className="text-[10px] text-emerald-700 uppercase">Set</div>
              <ul className="text-[12px] text-zinc-800 space-y-0.5">
                {Object.entries(change.aspects_set).map(([k, v]) => (
                  <li key={k}><span className="font-medium">{k}:</span> {Array.isArray(v) ? v.join(', ') : String(v)}</li>
                ))}
              </ul>
            </div>
          )}
          {change.aspects_remove?.length > 0 && (
            <div>
              <div className="text-[10px] text-red-600 uppercase">Remove</div>
              <ul className="text-[12px] text-zinc-800">
                {change.aspects_remove.map((k) => <li key={k}>{k}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          onClick={onApply}
          disabled={applying}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid={`ebay-chat-apply-${messageId}`}
        >
          {applying
            ? <><Loader2 size={11} className="mr-1.5 animate-spin" /> Applying…</>
            : <><Check size={11} className="mr-1.5" /> Apply to eBay</>}
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={onDismiss}
          disabled={applying}
          className="border-zinc-200 text-zinc-600"
          data-testid={`ebay-chat-dismiss-${messageId}`}
        >
          <XIcon size={11} className="mr-1.5" /> Dismiss
        </Button>
      </div>
    </div>
  );
}
